import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, Lock, ArrowRight,
} from "lucide-react";
import Logo from "@/components/Logo";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { Doodle } from "@/components/marketing/Decor";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr("This reset link is invalid. Please request a new one from the forgot-password page.");
    }
  }, [token]);

  const policyOk = (p) => p.length >= 6 && /[A-Za-z]/.test(p) && /[0-9!@#$%^&*_\-+=?]/.test(p);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) return;
    if (!policyOk(pw)) {
      setErr("Password must be at least 6 characters and include a letter and a number or symbol.");
      return;
    }
    if (pw !== pw2) { setErr("Passwords don’t match."); return; }
    setBusy(true);
    try {
      await api.resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate("/vendor/login", { replace: true }), 2200);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn’t reset password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-light mkt-root mkt-paper-noise min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <Toaster richColors position="top-center" />
      <Doodle.Star className="hidden md:block absolute right-10 top-12 h-10 w-10 text-black/70" />

      <div className="w-full max-w-md relative">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="City DOOH home" className="inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md"><Logo size={150} light /></Link>
        </div>
        <div className="mkt-sticker p-7 relative">
          <span className="mkt-tape absolute -top-4 left-6">New Password</span>

          {done ? (
            <div className="text-center mt-3" data-testid="reset-done-panel">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#d8f1c8] border-2 border-[#176b3a] grid place-items-center text-[#176b3a] mb-3">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="font-display uppercase text-2xl">Password updated</h1>
              <p className="text-sm text-black/70 mt-2">You can now sign in with your new password. Redirecting…</p>
              <Link to="/vendor/login" className="block mt-5">
                <button className="mkt-btn w-full justify-center">Go to sign in <ArrowRight className="h-4 w-4" /></button>
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center mt-3" data-testid="reset-missing-token">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#fbd5d5] border-2 border-[#8b1212] grid place-items-center text-[#8b1212] mb-3">
                <AlertTriangle className="h-7 w-7" />
              </div>
              <h1 className="font-display uppercase text-2xl">Invalid reset link</h1>
              <p className="text-sm text-black/70 mt-2">This link is missing or malformed. Please request a new password-reset email.</p>
              <Link to="/vendor/forgot-password" className="block mt-5">
                <button className="mkt-btn w-full justify-center">Request a new link <ArrowRight className="h-4 w-4" /></button>
              </Link>
              <Link to="/vendor/login" className="block mt-3">
                <button className="ag-btn-ghost w-full justify-center">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
                </button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display uppercase text-3xl mt-3 leading-none">
                Choose a <span className="mkt-highlight">new</span> password
              </h1>
              <p className="text-sm text-black/70 mt-2">
                Pick something you haven’t used before. Min 6 chars with a letter and a number/symbol.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="uppercase text-[11px] font-bold tracking-wider text-black">New password</Label>
                  <div className="relative">
                    <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/55" />
                    <Input
                      type={show ? "text" : "password"}
                      autoFocus
                      autoComplete="new-password"
                      className="pl-9 pr-10 h-11 border-2 border-black bg-white"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      data-testid="reset-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShow((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-black/55 hover:text-black"
                      tabIndex={-1}
                      aria-label={show ? "Hide password" : "Show password"}
                      data-testid="reset-toggle-visibility"
                    >
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Confirm new password</Label>
                  <div className="relative">
                    <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/55" />
                    <Input
                      type={show ? "text" : "password"}
                      autoComplete="new-password"
                      className="pl-9 h-11 border-2 border-black bg-white"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      data-testid="reset-confirm-password"
                    />
                  </div>
                </div>
                {err && (
                  <p className="text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2" data-testid="reset-error">{err}</p>
                )}
                <button type="submit" className="mkt-btn w-full justify-center" disabled={busy} data-testid="reset-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Reset password <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </form>
              <div className="text-sm text-black/70 mt-5 text-center">
                Changed your mind?{" "}
                <Link to="/vendor/login" className="font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2">
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
